// eslint-disable-next-line
import { getLocalTimeZone, Time, toCalendarDateTime, today, ZonedDateTime } from '@internationalized/date';
import { addMonths, format, getHours, getMinutes, isValid, startOfDay } from 'date-fns';
import { getTimezoneOffset, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { TFunction } from 'i18next';

export const localDateISOFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS";
export const localStartDateISOFormat = "yyyy-MM-dd'T00:00:00.000'";
export const UTC_TIMEZONE_NAME = 'UTC';

export const getTimeZone = () => {
  const computedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return computedTimeZone.toString();
};

export const getCalculatedHoursMinutes = (totalHours: number) => {
  const hours = Math.floor(totalHours);
  const minutes = Math.round((totalHours - hours) * 60);

  return { hours, minutes };
};

export const toNormalizedDate = (dateValue: string) => {
  const probablyNormalizedDate = new Date(dateValue);
  return isValid(probablyNormalizedDate) ? probablyNormalizedDate : undefined;
};

export const dateToStamp = (date?: string | Date | null, localeObj?: Locale) =>
  date ? Number(format(new Date(date), 'T', { locale: localeObj })) : undefined;

export const getTimeLineOffsetInMs = (date: number) => getHours(date) * 60 * 60 * 1000 + getMinutes(date) * 60 * 1000;

export const getNameOfMonthWithOffset = (startDate: Date, offset: number, localeObj?: Locale) =>
  format(addMonths(startDate, offset), 'LLLL', { locale: localeObj });

/**
 * Returns the difference, in minutes, between _zonedDate_'s _timezone_ and _UTC time_.
 *
 * [`getTimezoneOffset`](https://www.npmjs.com/package/date-fns-tz#gettimezoneoffset) returns number of milliseconds, however,
 * we need to make that value similar to output of [`Date.getTimezoneOffset()`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getTimezoneOffset)
 */
export const getZonedDateTimezoneOffset = (zonedDate: Date | number, timezone: string) => {
  const date = typeof zonedDate === 'number' ? utcToZonedTime(zonedDate, timezone) : zonedDate;
  const offset = getTimezoneOffset(timezone, date);

  if (offset === 0) {
    return offset;
  }

  return (offset / 60 / 1000) * -1;
};

/**
 * Returns the difference, in minutes, between _zonedDateTime_'s _timezone_ and _UTC time_.
 *
 * _ZonedDateTime.offset_ returns the UTC offset for this time, in milliseconds., however,
 * we need to make that value similar to output of [`Date.getTimezoneOffset()`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getTimezoneOffset)
 */
export const getZonedDateTimeOffset = (date: ZonedDateTime) => {
  const { offset } = date;

  if (offset === 0) {
    return offset;
  }

  return (offset / 60 / 1000) * -1;
};

export const getZonedOrLocalTimezoneOffset = (date: Date | number, timezone: string) => {
  const localTimezone = getTimeZone();

  if (localTimezone === timezone) {
    return new Date(date).getTimezoneOffset();
  }

  return getZonedDateTimezoneOffset(date, timezone);
};

export const getUTCHoursOffsetLabel = (date: Date, timezone: string, locale: string, t: TFunction) => {
  const offset = getTimezoneOffset(timezone, date) / 60 / 60 / 1000;
  const offsetLocalizedString = offset.toLocaleString(locale);

  const offsetLabel = offset < 0 ? offsetLocalizedString : `+${offsetLocalizedString}`;
  const hoursLabel = t('time.hour', { count: Number(Math.abs(offset)) });

  return `${t('timezone.utc')} ${offsetLabel} ${hoursLabel}`;
};

export const startOfDayInZone = (date: Date, timezone: string, localeObj?: Locale) => {
  const startDate = startOfDay(utcToZonedTime(date, timezone));
  const startOfDayZonedString = format(startDate, localStartDateISOFormat, { locale: localeObj });

  return zonedTimeToUtc(startOfDayZonedString, timezone);
};

export const toDateFromTime = (time: Time) => toCalendarDateTime(today(getLocalTimeZone()), time);
